import { createStore } from 'zustand/vanilla'
import { OIDC_AUTHORITY, OIDC_CLIENT_ID } from '../env'

/**
 * The access token, bridged out of React so plain `fetch` callers can reach it.
 *
 * A zustand *vanilla* store, matching the platform's other remotes. The React-bound store pulls
 * `useSyncExternalStore` through its own import graph, which under Module Federation can resolve
 * a second copy of React; the vanilla store read via `getState()` cannot. Do not "modernise" this
 * to `create()` — the comment in the reporting module's authStore records the same reason.
 *
 * In federated mode the shell owns the OIDC session and `TokenSync` mirrors the token here. In
 * standalone mode this app owns it, and `AuthProvider` does the same thing on every sign-in and
 * silent renew. Call sites cannot tell the difference, which is the point.
 */

interface AuthState {
  token: string | null
  setToken: (token: string | null) => void
}

/**
 * Recover a token that has already been persisted, so the first render after a reload is
 * authenticated rather than briefly anonymous.
 *
 * oidc-client-ts keeps its session under a key derived from the authority and client id. Reading it
 * *here*, at module load, is what makes the recovery raceless — a provider effect cannot run before
 * the components beneath it, whose own effects fire first and would otherwise issue their opening
 * requests with no token.
 */
function readInitialToken(): string | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const key = `oidc.user:${OIDC_AUTHORITY}:${OIDC_CLIENT_ID}`
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { access_token?: string }
    return parsed.access_token ?? null
  } catch {
    // Malformed or blocked storage: start anonymous; AuthProvider supplies the token shortly.
    return null
  }
}

export const authStore = createStore<AuthState>((set) => ({
  token: readInitialToken(),
  setToken: (token) => set({ token }),
}))

/** The current access token, or null. For non-React callers such as the API client. */
export function getAccessToken(): string | null {
  return authStore.getState().token
}

export function setAccessToken(token: string | null): void {
  authStore.getState().setToken(token)
}

/**
 * Signs in again — more recently, or more strongly — for an action the relay's step-up refused
 * (Plans/identity-consolidation.md). Registered by the session's provider; null outside one, where a
 * refusal is simply reported.
 */
type StepUp = (acrValues: string | null) => Promise<void>
let stepUpHandler: StepUp | null = null

export function setStepUpHandler(handler: StepUp | null): void {
  stepUpHandler = handler
}

export function getStepUpHandler(): StepUp | null {
  return stepUpHandler
}
